import React, { useState, useEffect } from "react";
import {
  getStudentDetails,
  getUnAllottedStudentDetails,
  getStudentEnquiries,
  updateFollowUp,
  getRevenueData,
  getUpcomingPayments,
} from "../api/StudentDetails";
import {
  Student,
  StudentEnquiry,
  RevenueData,
  UpcomingPayment,
} from "../types";
import "../styles/Dashboard.css";
import {
  getDashboardDetails,
  getUpcomingPaymentList,
  getSmsWalletBalance,
} from "../api/DashBoardDetails";
import {
  DashboardRequest,
  DashboardResponse,
  StudentValidityInfoResponse,
  SmsWalletResponse,
  SMS_WALLET_LOW_BALANCE_THRESHOLD,
} from "../interfaces/DashboardInterface";

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardResponse>({
    totalStudentCount: 0,
    allottedStudentCount: 0,
    unAllottedStudentCount: 0,
    totalChairCount: 120,
    availableChairCount: 120,
    thisMonthEnquiries: 0,
    thisMonthRevenue: 0,
    totalRevenue: 0,
    activeStudent: 0,
    inactiveStudent: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentStudents, setRecentStudents] = useState<Student[]>([]);
  const [recentEnquiries, setRecentEnquiries] = useState<StudentEnquiry[]>([]);
  const [selectedEnquiry, setSelectedEnquiry] = useState<StudentEnquiry | null>(
    null
  );

  const [request, setRequest] = useState<DashboardRequest>({
    isTotalStudent: true,
    isOccupiedStudent: true,
    isAvailableChair: true,
    isThisMonthEnquiries: true,
    isThisMonthRevenue: true,
    isTotalRevenue: true,
    isActiveStudent: true,
    isInactiveStudent: true,
  });
  const [showEnquiryPopup, setShowEnquiryPopup] = useState(false);
  const [revenueTab, setRevenueTab] = useState<"total" | "monthly">("total");
  const [revenueData, setRevenueData] = useState<RevenueData>({
    totalRevenue: 0,
    thisMonthRevenue: 0,
    monthlyRevenue: [],
  });
  const [upcomingPayments, setUpcomingPayments] = useState<
    StudentValidityInfoResponse[]
  >([]);
  const [revenueLoading, setRevenueLoading] = useState<boolean>(false);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(6); // Default to 6 months
  const [selectedPayment, setSelectedPayment] =
    useState<StudentValidityInfoResponse | null>(null);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [smsWallet, setSmsWallet] = useState<SmsWalletResponse>({
    walletBalance: 0,
    smsCount: 0,
  });
  const [smsWalletLoading, setSmsWalletLoading] = useState(true);
  const [smsWalletError, setSmsWalletError] = useState<string | null>(null);

  const isSmsWalletLow =
    !smsWalletLoading &&
    !smsWalletError &&
    smsWallet.walletBalance <= SMS_WALLET_LOW_BALANCE_THRESHOLD;

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const dashboardResponse = await getDashboardDetails(request);
        const data = dashboardResponse.data;

        // Fetch enquiries
        let allEnquiries: StudentEnquiry[] = [];
        try {
          const enquiriesResponse = await getStudentEnquiries(true);
          allEnquiries = enquiriesResponse.data || [];
        } catch (error) {
          console.log("Using dummy enquiry data");
        }

        // Filter enquiries for this month
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();

        const thisMonthEnquiries = allEnquiries.filter((enquiry) => {
          if (!enquiry.createdDate) return false;
          const enquiryDate = new Date(enquiry.createdDate);
          return (
            enquiryDate.getMonth() === currentMonth &&
            enquiryDate.getFullYear() === currentYear
          );
        });

        const totalStudents = data.totalStudentCount;

        setStats({
          totalStudentCount: totalStudents,
          allottedStudentCount: data.allottedStudentCount,
          unAllottedStudentCount: data.unAllottedStudentCount,
          totalChairCount: data.totalChairCount,
          availableChairCount: data.availableChairCount,
          thisMonthEnquiries: thisMonthEnquiries.length,
          thisMonthRevenue: data.thisMonthRevenue,
          totalRevenue: data.totalRevenue,
          activeStudent: data.activeStudent || 0,
          inactiveStudent: data.inactiveStudent || 0,
        });

        try {
          setSmsWalletLoading(true);
          setSmsWalletError(null);
          const walletResponse = await getSmsWalletBalance();
          if (walletResponse.success && walletResponse.data) {
            setSmsWallet(walletResponse.data);
          } else {
            setSmsWalletError(walletResponse.message || "Unable to load SMS balance");
          }
        } catch {
          setSmsWalletError("Unable to load SMS balance");
        } finally {
          setSmsWalletLoading(false);
        }

        // Set recent students (last 5 allotted students)
        // use the allotted students list from the response (fallback to empty array)
        setRecentStudents(
          Array.isArray((data as any).allottedStudents)
            ? (data as any).allottedStudents.slice(0, 5)
            : []
        );

        // Set recent enquiries (last 5 enquiries)
        setRecentEnquiries(allEnquiries.slice(0, 5));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchRevenueData = async () => {
      try {
        setRevenueLoading(true);
        const response = await getRevenueData();
        if (response?.data) {
          setRevenueData(response.data);
        }
      } catch (error) {
        console.error("Error fetching revenue data:", error);
        // Set dummy data for development
        const dummyMonthlyRevenue = [];
        const now = new Date();
        for (let i = 5; i >= 0; i--) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
          dummyMonthlyRevenue.push({
            month: date.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            }),
            revenue: Math.floor(Math.random() * 50000) + 10000,
          });
        }
        setRevenueData({
          totalRevenue: dummyMonthlyRevenue.reduce(
            (sum, m) => sum + m.revenue,
            0
          ),
          thisMonthRevenue:
            dummyMonthlyRevenue[dummyMonthlyRevenue.length - 1]?.revenue || 0,
          monthlyRevenue: dummyMonthlyRevenue,
        });
      } finally {
        setRevenueLoading(false);
      }
    };

    const fetchUpcomingPayments = async () => {
      try {
        const response = await getUpcomingPaymentList();
        if (response?.data) {
          setUpcomingPayments(response.data);
        }
      } catch (error) {
        console.error("Error fetching upcoming payments:", error);
        // Set dummy data for development
        setUpcomingPayments([
          {
            fullName: "John Doe",
            mobileNumber: "9876543210",
            lastDate: new Date(
              Date.now() + 5 * 24 * 60 * 60 * 1000
            ).toISOString(),
            remainingDays: 5,
          },
          {
            fullName: "Jane Smith",
            mobileNumber: "9876543211",
            lastDate: new Date(
              Date.now() + 10 * 24 * 60 * 60 * 1000
            ).toISOString(),
            remainingDays: 10,
          },
        ]);
      }
    };

    fetchDashboardData();
    fetchRevenueData();
    fetchUpcomingPayments();
  }, []);

  const handleEnquiryClick = (enquiry: StudentEnquiry) => {
    setSelectedEnquiry(enquiry);
    setShowEnquiryPopup(true);
  };

  const closeEnquiryPopup = () => {
    setShowEnquiryPopup(false);
    setSelectedEnquiry(null);
  };

  const handlePaymentClick = (payment: StudentValidityInfoResponse) => {
    setSelectedPayment(payment);
    setShowPaymentPopup(true);
  };

  const closePaymentPopup = () => {
    setShowPaymentPopup(false);
    setSelectedPayment(null);
  };

  const handleFollowupDone = async () => {
    try {
      await updateFollowUp(selectedEnquiry?.mobileNumber || "0000000000");

      // Refetch enquiries to update the Recent Enquiries block
      const enquiriesResponse = await getStudentEnquiries(true);
      const allEnquiries = enquiriesResponse.data || [];

      // Update recent enquiries (last 5 enquiries)
      setRecentEnquiries(allEnquiries.slice(0, 5));

      // Close the modal
      closeEnquiryPopup();
    } catch (error) {
      console.error("Error updating follow-up:", error);
      // Still close the modal even if there's an error
      closeEnquiryPopup();
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard Overview</h1>
        <p>Welcome to the Library Management System</p>
      </div>

      {isSmsWalletLow && (
        <div className="sms-wallet-alert" role="alert">
          <div className="sms-wallet-alert-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 9v4M12 17h.01"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="sms-wallet-alert-content">
            <h4>Low SMS Wallet Balance</h4>
            <p>
              Your Fast2SMS balance is ₹
              {smsWallet.walletBalance.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              . Please recharge to keep registration and payment SMS
              notifications working.
            </p>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon students">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="9"
                cy="7"
                r="4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="stat-content">
            <h3>Total Students</h3>
            <p className="stat-number">{stats.totalStudentCount}</p>
            <span className="stat-label">Registered users</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon occupied">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <polyline
                points="9,22 9,12 15,12 15,22"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="stat-content">
            <h3>Occupied Chairs</h3>
            <p className="stat-number">{stats.allottedStudentCount}</p>
            <span className="stat-label">Currently in use</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon available">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="2"
                ry="2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 9h6v6H9z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="stat-content">
            <h3>Available Chairs</h3>
            <p className="stat-number">{stats.availableChairCount}</p>
            <span className="stat-label">Ready for allocation</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon pending">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 6v6l4 2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="stat-content">
            <h3>Pending Allocations</h3>
            <p className="stat-number">{stats.unAllottedStudentCount}</p>
            <span className="stat-label">Awaiting seats</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon enquiries">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <polyline
                points="22,6 12,13 2,6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="stat-content">
            <h3>This Month Enquiries</h3>
            <p className="stat-number">{stats.thisMonthEnquiries}</p>
            <span className="stat-label">New enquiries</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon active">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17l-5-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="stat-content">
            <h3>Active Students</h3>
            <p className="stat-number">{stats.activeStudent || 0}</p>
            <span className="stat-label">Currently active</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon inactive">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="stat-content">
            <h3>Inactive Students</h3>
            <p className="stat-number">{stats.inactiveStudent || 0}</p>
            <span className="stat-label">Currently inactive</span>
          </div>
        </div>

        <div className={`stat-card${isSmsWalletLow ? " stat-card-warning" : ""}`}>
          <div className={`stat-icon wallet${isSmsWalletLow ? " wallet-low" : ""}`}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 12V7H5a2 2 0 0 1 0-4h14v4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 5v14a2 2 0 0 0 2 2h16v-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18 12a2 2 0 0 0 0 4h4v-4h-4z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="stat-content">
            <h3>SMS Wallet</h3>
            <p className="stat-number">
              {smsWalletLoading
                ? "..."
                : smsWalletError
                ? "--"
                : `₹${smsWallet.walletBalance.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
            </p>
            <span className="stat-label">
              {smsWalletLoading
                ? "Loading balance..."
                : smsWalletError
                ? smsWalletError
                : isSmsWalletLow
                ? "Low balance — recharge soon"
                : `${smsWallet.smsCount.toLocaleString("en-IN")} SMS available`}
            </span>
          </div>
        </div>
      </div>

      {/* Revenue Section */}
      <div className="dashboard-card revenue-section">
        <div className="card-header">
          <h3>Revenue Overview</h3>
          <div className="revenue-tabs">
            <button
              className={`revenue-tab ${
                revenueTab === "total" ? "active" : ""
              }`}
              onClick={() => setRevenueTab("total")}
            >
              Total Revenue
            </button>
            <button
              className={`revenue-tab ${
                revenueTab === "monthly" ? "active" : ""
              }`}
              onClick={() => setRevenueTab("monthly")}
            >
              This Month Revenue
            </button>
          </div>
        </div>
        <div className="revenue-content">
          {revenueTab === "total" ? (
            <div className="revenue-display">
              <div className="revenue-amount">
                ₹{stats.totalRevenue.toLocaleString("en-IN")}
              </div>
              <p className="revenue-label">Total Revenue</p>
            </div>
          ) : (
            <div className="revenue-display">
              <div className="revenue-amount">
                ₹{stats.thisMonthRevenue.toLocaleString("en-IN")}
              </div>
              <p className="revenue-label">This Month Revenue</p>
            </div>
          )}
        </div>
      </div>


      {/* Revenue Chart - Time Period Selector */}
      <div className="dashboard-card revenue-chart-card">
        <div className="card-header">
          <h3>Revenue Trend</h3>
          <div className="time-period-selector">
            <label htmlFor="timePeriod">Time Period: </label>
            <select
              id="timePeriod"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(Number(e.target.value))}
              className="period-select"
            >
              <option value={1}>Last 1 Month</option>
              <option value={2}>Last 2 Months</option>
              <option value={3}>Last 3 Months</option>
              <option value={6}>Last 6 Months</option>
              <option value={12}>Last 1 Year</option>
            </select>
          </div>
        </div>
        <div className="revenue-chart-container">
          {revenueLoading ? (
            <div className="chart-loading">
              <div className="spinner"></div>
              <p>Loading chart data...</p>
            </div>
          ) : (
            <div className="revenue-line-chart">
              {(() => {
                // Filter revenue data based on selected period
                const filteredRevenue = revenueData.monthlyRevenue.slice(
                  -selectedPeriod
                );

                return filteredRevenue.length > 0 ? (
                  <svg
                    viewBox="0 0 600 300"
                    className="chart-svg"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    {/* Grid lines */}
                    {[0, 1, 2, 3, 4].map((i) => (
                      <line
                        key={i}
                        x1="50"
                        y1={50 + i * 50}
                        x2="550"
                        y2={50 + i * 50}
                        stroke="#e2e8f0"
                        strokeWidth="1"
                      />
                    ))}
                    {/* Revenue line */}
                    <polyline
                      points={filteredRevenue
                        .map((month, index) => {
                          const x =
                            50 +
                            (index * 500) / (filteredRevenue.length - 1 || 1);
                          const maxRevenue = Math.max(
                            ...filteredRevenue.map((m) => m.revenue),
                            1
                          );
                          const y = 250 - (month.revenue / maxRevenue) * 200;
                          return `${x},${y}`;
                        })
                        .join(" ")}
                      fill="none"
                      stroke="#2563eb"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {/* Data points */}
                    {filteredRevenue.map((month, index) => {
                      const x =
                        50 + (index * 500) / (filteredRevenue.length - 1 || 1);
                      const maxRevenue = Math.max(
                        ...filteredRevenue.map((m) => m.revenue),
                        1
                      );
                      const y = 250 - (month.revenue / maxRevenue) * 200;
                      return (
                        <g key={index}>
                          <circle
                            cx={x}
                            cy={y}
                            r="5"
                            fill="#2563eb"
                            stroke="white"
                            strokeWidth="2"
                          />
                          <text
                            x={x}
                            y={y - 15}
                            textAnchor="middle"
                            fontSize="10"
                            fill="#64748b"
                            fontWeight="600"
                          >
                            ₹{(month.revenue / 1000).toFixed(0)}k
                          </text>
                        </g>
                      );
                    })}
                    {/* X-axis labels */}
                    {filteredRevenue.map((month, index) => {
                      const x =
                        50 + (index * 500) / (filteredRevenue.length - 1 || 1);
                      return (
                        <text
                          key={index}
                          x={x}
                          y={280}
                          textAnchor="middle"
                          fontSize="11"
                          fill="#64748b"
                          fontWeight="500"
                        >
                          {month.month}
                        </text>
                      );
                    })}
                  </svg>
                ) : (
                  <div className="no-data">
                    <p>No revenue data available</p>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Payments Table */}
      <div className="dashboard-card upcoming-payments-card">
        <div className="card-header">
          <h3>Upcoming Payments</h3>
          <span className="card-subtitle">Payments due soon</span>
        </div>
        <div className="upcoming-payments-table-container">
          {upcomingPayments.length === 0 ? (
            <div className="no-data">
              <p>No upcoming payments</p>
            </div>
          ) : (
            <table className="upcoming-payments-table">
              <thead>
                <tr>
                  <th className="mobile-visible">Full Name</th>
                  <th className="mobile-hidden">Mobile Number</th>
                  <th className="mobile-hidden">Last Date</th>
                  <th>Remaining Days</th>
                </tr>
              </thead>
              <tbody>
                {upcomingPayments.map((payment) => {
                  const lastDate = new Date(payment.lastDate);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  lastDate.setHours(0, 0, 0, 0);
                  const daysRemaining =
                    payment.remainingDays !== undefined
                      ? payment.remainingDays
                      : Math.ceil(
                          (lastDate.getTime() - today.getTime()) /
                            (1000 * 60 * 60 * 24)
                        );
                  const isUrgent = daysRemaining <= 7 && daysRemaining >= 0;
                  const isOverdue = daysRemaining < 0;

                  return (
                    <tr
                      key={payment.mobileNumber}
                      className="payment-row clickable"
                      onClick={() => handlePaymentClick(payment)}
                    >
                      <td className="mobile-visible">
                        <div className="payment-student-name">
                          {payment.fullName}
                        </div>
                      </td>
                      <td className="mobile-hidden">{payment.mobileNumber}</td>
                      <td className="mobile-hidden">
                        {lastDate.toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </td>
                      <td>
                        <span
                          className={`remaining-days-badge ${
                            isOverdue
                              ? "overdue"
                              : isUrgent
                              ? "urgent"
                              : "normal"
                          }`}
                        >
                          {isOverdue
                            ? `Overdue by ${Math.abs(daysRemaining)} days`
                            : `${daysRemaining} days`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Chair Occupancy Chart */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Chair Occupancy</h3>
            <div className="occupancy-percentage">
              {Math.round(
                (stats.allottedStudentCount / stats.totalChairCount) * 100
              )}
              %
            </div>
          </div>
          <div className="occupancy-chart">
            <div className="chart-bar">
              <div
                className="chart-fill occupied"
                style={{
                  width: `${
                    (stats.allottedStudentCount / stats.totalChairCount) * 100
                  }%`,
                }}
              ></div>
            </div>
            <div className="chart-labels">
              <span>Occupied: {stats.allottedStudentCount}</span>
              <span>Available: {stats.totalChairCount}</span>
            </div>
          </div>
        </div>

        {/* Recent Students */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Recent Allocations</h3>
            <span className="card-subtitle">Last 5 students</span>
          </div>
          <div className="recent-students">
            {recentStudents.length > 0 ? (
              recentStudents.map((student, index) => (
                <div key={index} className="student-item">
                  <div className="student-avatar">
                    {student.firstName?.charAt(0)}
                    {student.lastName?.charAt(0)}
                  </div>
                  <div className="student-info">
                    <h4>
                      {student.firstName} {student.lastName}
                    </h4>
                    <p>Chair #{student.chairNumber || "N/A"}</p>
                  </div>
                  <div className="student-status">
                    <span className="status-badge active">Active</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">
                <p>No recent allocations</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="quick-actions">
            <button className="action-btn primary">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 5v14M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Allocate Seat
            </button>
            <button className="action-btn secondary">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              View Reports
            </button>
            <button className="action-btn secondary">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM19 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Manage Students
            </button>
          </div>
        </div>

        {/* Recent Enquiries */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Recent Enquiries</h3>
            <span className="card-subtitle">Last 5 enquiries</span>
          </div>
          <div className="recent-enquiries">
            {recentEnquiries.length > 0 ? (
              recentEnquiries.map((enquiry, index) => (
                <div
                  key={index}
                  className="enquiry-item clickable"
                  onClick={() => handleEnquiryClick(enquiry)}
                >
                  <div className="enquiry-avatar">
                    {enquiry.firstName?.charAt(0)}
                    {enquiry.lastName?.charAt(0)}
                  </div>
                  <div className="enquiry-info">
                    <div className="enquiry-name-date">
                      <h4>
                        {enquiry.firstName} {enquiry.lastName}
                      </h4>
                      <span className="enquiry-date">
                        {enquiry.createdDate
                          ? new Date(enquiry.createdDate).toLocaleDateString()
                          : "Recently"}
                      </span>
                    </div>
                    <div className="enquiry-mobile">
                      <p>{enquiry.mobileNumber}</p>
                    </div>
                  </div>
                  <div className="enquiry-arrow">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 18l6-6-6-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">
                <p>No recent enquiries</p>
              </div>
            )}
          </div>
        </div>

        {/* System Status */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>System Status</h3>
          </div>
          <div className="system-status">
            <div className="status-item">
              <div className="status-indicator online"></div>
              <span>API Connection</span>
              <span className="status-value">Online</span>
            </div>
            <div className="status-item">
              <div className="status-indicator online"></div>
              <span>Database</span>
              <span className="status-value">Connected</span>
            </div>
            <div className="status-item">
              <div className="status-indicator online"></div>
              <span>Authentication</span>
              <span className="status-value">Active</span>
            </div>
            <div className="status-item">
              <div
                className={`status-indicator ${
                  isSmsWalletLow ? "offline" : "online"
                }`}
              ></div>
              <span>SMS Wallet</span>
              <span className="status-value">
                {smsWalletLoading
                  ? "Checking..."
                  : smsWalletError
                  ? "Unavailable"
                  : isSmsWalletLow
                  ? "Low Balance"
                  : "Healthy"}
              </span>
            </div>
            <div className="status-item">
              <div className="status-indicator warning"></div>
              <span>Last Backup</span>
              <span className="status-value">2 hours ago</span>
            </div>
          </div>
        </div>
      </div>

      {/* Enquiry Details Popup */}
      {showEnquiryPopup && selectedEnquiry && (
        <div className="enquiry-popup-overlay" onClick={closeEnquiryPopup}>
          <div className="enquiry-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Enquiry Details</h3>
              <button className="close-btn" onClick={closeEnquiryPopup}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div className="popup-content">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Name</label>
                  <span>
                    {selectedEnquiry.firstName} {selectedEnquiry.lastName}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Mobile Number</label>
                  <span>{selectedEnquiry.mobileNumber}</span>
                </div>
                <div className="detail-item">
                  <label>Address</label>
                  <span>{selectedEnquiry.address}</span>
                </div>
                <div className="detail-item">
                  <label>Submitted</label>
                  <span>
                    {selectedEnquiry.createdDate
                      ? new Date(selectedEnquiry.createdDate).toLocaleString()
                      : "Recently"}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Created By</label>
                  <span>{selectedEnquiry.createdBy}</span>
                </div>
              </div>
            </div>
            <div className="popup-footer">
              <button className="btn-followup" onClick={handleFollowupDone}>
                Follow-up Done
              </button>
              <button className="btn-secondary" onClick={closeEnquiryPopup}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Details Popup */}
      {showPaymentPopup && selectedPayment && (
        <div className="enquiry-popup-overlay" onClick={closePaymentPopup}>
          <div className="enquiry-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Payment Details</h3>
              <button className="close-btn" onClick={closePaymentPopup}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div className="popup-content">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Full Name</label>
                  <span>{selectedPayment.fullName}</span>
                </div>
                <div className="detail-item">
                  <label>Mobile Number</label>
                  <a href={`tel:${selectedPayment.mobileNumber}`}>
                    <span>{selectedPayment.mobileNumber}</span>
                  </a>
                </div>
                <div className="detail-item">
                  <label>Last Date</label>
                  <span>
                    {new Date(selectedPayment.lastDate).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Remaining Days</label>
                  <span>
                    {(() => {
                      const lastDate = new Date(selectedPayment.lastDate);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      lastDate.setHours(0, 0, 0, 0);
                      const daysRemaining =
                        selectedPayment.remainingDays !== undefined
                          ? selectedPayment.remainingDays
                          : Math.ceil(
                              (lastDate.getTime() - today.getTime()) /
                                (1000 * 60 * 60 * 24)
                            );
                      const isOverdue = daysRemaining < 0;
                      return isOverdue
                        ? `Overdue by ${Math.abs(daysRemaining)} days`
                        : `${daysRemaining} days`;
                    })()}
                  </span>
                </div>
              </div>
            </div>
            <div className="popup-footer">
              <button className="btn-secondary" onClick={closePaymentPopup}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
